"use client"

import { useState, useEffect, type ReactNode } from "react"
import { usePathname } from "next/navigation"
import { LoadingScreen } from "./loading-screen"

interface AppLoaderProps {
  children: ReactNode
}

export function AppLoader({ children }: AppLoaderProps) {
  const pathname = usePathname()
  // Start with null to prevent hydration mismatch
  // Server and client both render children initially
  const [mounted, setMounted] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(false)
  const [showLoader, setShowLoader] = useState(false)

  useEffect(() => {
    setMounted(true)

    // v1 loading splash applies ONLY to legacy v1 routes; every v3 surface
    // renders real text immediately (LCP = real copy, hero-convergence spec).
    const v1Mode = pathname?.startsWith("/v1-archive") || pathname?.startsWith("/work")
    if (!v1Mode) {
      setIsFirstLoad(false)
      setShowLoader(false)
      return
    }

    // Check if this is the first visit in this session
    const hasVisited = sessionStorage.getItem("hasVisited")

    if (!hasVisited) {
      // First visit - show loader
      setIsFirstLoad(true)
      setShowLoader(true)
      sessionStorage.setItem("hasVisited", "true")
    }
    // If hasVisited exists, keep showLoader=false (default)
  }, [pathname])

  const handleLoadingComplete = () => {
    setShowLoader(false)
    setIsFirstLoad(false)
  }

  // Before mount, render children directly (matches server render)
  if (!mounted) {
    return <>{children}</>
  }

  return (
    <>
      {showLoader && isFirstLoad && (
        <LoadingScreen onComplete={handleLoadingComplete} minDuration={1400} />
      )}
      <div
        style={{
          opacity: showLoader && isFirstLoad ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        {children}
      </div>
    </>
  )
}
